// Breaks the infinite "protected page -> 401 -> forced logout -> login -> same page -> 401" loop.
//
// Desired behavior (kept): after login, send the user to the page they were headed to
// (location.state.from) — e.g. a report URL a teammate shared, so they land where they intended.
// But if that page immediately bounces them back to login (a 401 the axios interceptor turns into a
// logout), returning them there again just loops forever and the only escape is a brand-new tab.
//
// So: if we already tried to redirect to this exact destination within LOOP_WINDOW_MS, treat it as a
// loop and fall back to the safe default landing instead.
//
// State lives in sessionStorage so it survives the interceptor's window.location.reload() within the
// same tab, but a fresh tab/window starts clean — which is why opening a new tab has always worked.

const STORAGE_KEY = 'postLoginRedirect';
const LOOP_WINDOW_MS = 30 * 1000;

export const resolvePostLoginRedirect = (from, defaultPath) => {
   // A destination from the sign-in query string must stay within this app.
   if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')
      || from.includes('\\') || from === '/' || from.startsWith('/auth/')) {
      return defaultPath;
   }

   let previous = null;
   try {
      previous = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
   } catch (e) {
      previous = null;
   }

   const now = Date.now();
   const isLoopingBack = previous
      && previous.path === from
      && (now - previous.at) < LOOP_WINDOW_MS;

   // Record this attempt (re-stamped each time so a rapid bounce sequence keeps sliding the window).
   try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path: from, at: now }));
   } catch (e) {
      // sessionStorage unavailable (private mode / disabled) -> fail open to normal behavior.
   }

   // Second attempt at the same destination within the window => it's bouncing them. Break the loop
   // by sending them to the default landing instead of back into the failing page.
   return isLoopingBack ? defaultPath : from;
};

// BrowserRouter owns /t. Preserve the current route and filters across a full navigation to
// sign-in after the gateway has rejected both the access token and its refresh attempt.
export const jobcaseSignInUrl = (pathname, search = '') => {
   const route = pathname?.startsWith('/t/') ? pathname.slice(2) : pathname;
   const from = route && route.startsWith('/') && !route.startsWith('//')
      && !route.startsWith('/auth/') ? `${ route }${ search }` : null;
   return `/t/auth/login/jobcase${ from ? `?from=${ encodeURIComponent(from) }` : '' }`;
};
